import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { Mail, Pencil, Plus, Trash2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, Skeleton } from "@fleet/ui";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@habits/shared";
import { useI18n } from "@/providers/i18n-provider";
import { EmailTemplateEditor } from "./email-template-editor";
import { QueryErrorState } from "@/components/ui/query-error-state";

type EmailTemplate = components["schemas"]["EmailTemplate"];

/**
 * CRUD for email templates (campaign #11). Lists system and custom templates,
 * and allows creating/editing/deleting them. System templates cannot be deleted.
 */
export function EmailTemplateManager() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  const templatesQuery = createApiQuery<EmailTemplate[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "email", "templates"],
    async () => api.GET("/api/admin/email/templates"),
    () => ({ staleTime: 30_000 }),
  );

  // Addressable template editor via URL (#15b D15/D16): `?compose=new`
  // (create) or `?tpl=<id>` (edit). Editor reopens accurately on reload/share;
  // editing resolves permission-aware from list (missing id → does not open).
  const creating = () => (search() as { compose?: string }).compose === "new";
  const editId = () => (search() as { tpl?: string }).tpl;
  const editing = createMemo<EmailTemplate | null>(() => {
    const id = editId();
    if (!id) {
      return null;
    }
    return (templatesQuery.data ?? []).find((tpl) => tpl.id === id) ?? null;
  });

  const openCreate = () => {
    void navigate({
      to: "/admin/email",
      search: { compose: "new", tpl: undefined },
      replace: true,
    });
  };
  const openEdit = (tpl: EmailTemplate) => {
    void navigate({
      to: "/admin/email",
      search: { tpl: tpl.id, compose: undefined },
      replace: true,
    });
  };
  const closeEditor = () => {
    void navigate({
      to: "/admin/email",
      search: { compose: undefined, tpl: undefined },
      replace: true,
    });
  };

  const deleteMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/admin/email/templates/delete", { body: vars }),
  );

  const handleDelete = async (tpl: EmailTemplate) => {
    if (tpl.isSystem) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({ id: tpl.id });
      await templatesQuery.refetch();
    } catch (err) {
      console.error("Error deleting email template:", err);
    }
  };

  const onSaved = async () => {
    closeEditor();
    await templatesQuery.refetch();
  };

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Mail class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Email Templates`)}</h2>
        </div>
        <Button class="gap-2" onClick={openCreate}>
          <Plus class="h-4 w-4" />
          {t(msg`New Template`)}
        </Button>
      </div>

      <Show when={creating() || editing()}>
        <EmailTemplateEditor template={editing()} onSaved={onSaved} onCancel={closeEditor} />
      </Show>

      {/* P23 / CC-60 . I3: <<no hay respuesta>> != <<no hay nada>>. En error
          `isLoading`/`isPending` son FALSE, asi que el guard de carga deja pasar,
          `data` es `undefined` y el `?? []` lo convierte en lista vacia: la
          pantalla dice <<no hay nada>> cuando el API simplemente no respondio.
          El bloque se ENVUELVE; encadenar `&& !isError` estaria MAL, porque en
          error el <Show> pintaria su fallback y quedarian la caja de error Y el
          esqueleto a la vez, para siempre. */}
      <Show when={templatesQuery.isError}>
        <QueryErrorState
          testId="email-templates-error"
          compact
          onRetry={() => void templatesQuery.refetch()}
        />
      </Show>
      <Show when={!templatesQuery.isError}>
        <Show
          when={!templatesQuery.isLoading}
          fallback={
            <div class="space-y-3">
              <For each={Array.from({ length: 4 }, () => 0)}>
                {() => <Skeleton class="h-20 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <div class="space-y-3">
            <For each={templatesQuery.data ?? []}>
              {(tpl) => (
                <div class="flex items-center justify-between rounded-xl border border-border bg-card p-4">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="truncate font-semibold">{tpl.name}</span>
                      <Badge variant="secondary">{tpl.category}</Badge>
                      <Show when={tpl.isSystem}>
                        <Badge>{t(msg`System`)}</Badge>
                      </Show>
                      <Show when={!tpl.isActive}>
                        <Badge variant="outline">{t(msg`Inactive`)}</Badge>
                      </Show>
                    </div>
                    <p class="mt-1 truncate text-sm text-muted-foreground">{tpl.subject}</p>
                    <p class="mt-0.5 text-xs text-muted-foreground">
                      {(() => {
                        const { key } = tpl;
                        const version = tpl.currentVersion;
                        return t(msg`Key: ${key} · v${version}`);
                      })()}
                    </p>
                  </div>
                  <div class="flex shrink-0 items-center gap-2">
                    <Button variant="ghost" size="sm" class="gap-1" onClick={() => openEdit(tpl)}>
                      <Pencil class="h-4 w-4" />
                      {t(msg`Edit`)}
                    </Button>
                    <Show when={!tpl.isSystem}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="gap-1 text-destructive"
                        disabled={deleteMutation.isPending}
                        onClick={() => handleDelete(tpl)}
                      >
                        <Trash2 class="h-4 w-4" />
                        {t(msg`Delete`)}
                      </Button>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
